import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Vote, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useTier, TIER_META } from "../lib/tiers";
import { useAuth } from "../context/AuthContext";
import { VacationCard } from "../components/VacationCard";
import { TierSwitch } from "../components/TierSwitch";
import { LocationFilters, applyFilters, emptyFilters } from "../components/LocationFilters";

const CATS = [["all", "All"], ["vacation", "Vacations"], ["concert", "Concerts"], ["experience", "Experiences"]];

export default function VacationsPage() {
  const { user } = useAuth();
  const [tier, setTier] = useTier();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [stats, setStats] = useState(null);
  const [me, setMe] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);

  const load = useCallback(() => {
    api.get(`/vacations?tier=${tier}`).then((r) => setItems(r.data));
    api.get("/public/stats").then((r) => setStats(r.data));
    if (user && user.role !== "admin") api.get("/me/status").then((r) => setMe(r.data));
  }, [user, tier]);
  useEffect(() => { load(); }, [load]);

  const mem = me?.tiers?.[tier]?.membership;
  const canVote = mem?.status === "active";
  const nextId = stats?.[tier]?.next_vacation?.id;
  const vote = async (id) => {
    try { await api.post("/votes", { vacation_id: id }); toast.success("Vote counted"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const shown = applyFilters(items.filter((v) => cat === "all" || v.category === cat), filters);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-10 pt-32 pb-24">
      <p className="eyebrow">Curated Destinations & Experiences</p>
      <h1 className="font-serif-lux text-4xl sm:text-5xl mt-3">Where the lines lead</h1>
      <p className="text-slate-400 mt-3 max-w-xl text-sm md:text-base">Each line has its own catalog. Paid-up members vote on their line's next reward.</p>
      <TierSwitch value={tier} onChange={setTier} className="mt-8" />
      <p data-testid="tier-blurb" className="text-sm text-slate-500 mt-3">{TIER_META[tier].blurb}</p>
      <div className="mt-6 flex flex-wrap gap-2" data-testid="category-filter">
        {CATS.map(([k, l]) => (
          <button key={k} data-testid={`category-${k}`} onClick={() => setCat(k)}
            className={`px-4 py-1.5 rounded-full text-xs border transition-colors ${cat === k ? "border-[#38BDF8] text-[#38BDF8]" : "border-white/10 text-slate-400 hover:border-[#38BDF8]"}`}>{l}</button>
        ))}
      </div>
      <LocationFilters items={items} value={filters} onChange={setFilters} />
      <p data-testid="vacations-count" className="mt-4 text-xs text-slate-500 font-mono-lux">{shown.length} of {items.length} trips</p>
      <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vacations-grid">
        {shown.map((v) => (
          <VacationCard key={v.id} v={v} highlight={v.id === nextId} actions={
            <div className="flex items-center justify-between w-full">
              <span data-testid={`vote-count-${v.id}`} className="font-mono-lux text-xs text-slate-400">{v.votes} vote{v.votes === 1 ? "" : "s"}</span>
              {canVote && (mem.vote_vacation_id === v.id
                ? <span data-testid={`voted-${v.id}`} className="text-xs text-[#38BDF8] inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" />Your vote</span>
                : <button data-testid={`vote-btn-${v.id}`} onClick={() => vote(v.id)} className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-1"><Vote className="w-3.5 h-3.5" />Vote</button>)}
            </div>
          } />
        ))}
      </div>
      {shown.length === 0 && <p className="mt-10 text-slate-500">{items.length ? "No trips match these filters." : "New options are being sourced. Check back soon."}</p>}
    </div>
  );
}
